import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useOperationalExpense } from '@/hooks/useOperationalExpenses'
import { Button } from '@/components/ui/button'
import { formatBRL } from '@/lib/currency'

const METHOD_LABELS: Record<string, string> = {
  PIX: 'PIX',
  BANK_TRANSFER: 'Transferência',
  BANK_SLIP: 'Boleto',
  CASH: 'Dinheiro',
  OTHER: 'Outro',
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR')
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="sm:col-span-2 text-sm text-foreground">{value}</span>
    </div>
  )
}

export function OperationalExpenseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useOperationalExpense(id ?? '')

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Carregando…</p>
  }
  if (!data) {
    return <p className="text-sm text-muted-foreground">Despesa não encontrada.</p>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Link to="/operational-expenses">
          <Button variant="ghost" size="sm">
            <ArrowLeft size={16} />
            Voltar
          </Button>
        </Link>
      </div>

      <div>
        <h1 className="text-xl font-semibold text-foreground">
          E-{data.code} · {data.description}
        </h1>
        <p className="text-sm text-muted-foreground">
          Despesa imutável — para corrigir, registre um lançamento manual oposto no caixa.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <Row label="Categoria" value={data.category.name} />
        <Row label="Valor" value={<span className="font-medium tabular-nums text-red-700">−{formatBRL(data.amount)}</span>} />
        <Row label="Data" value={formatDate(data.date)} />
        <Row label="Conta paga" value={data.account.name} />
        <Row label="Forma de pagamento" value={METHOD_LABELS[data.method] ?? data.method} />
        <Row label="Veículo" value={data.vehicle?.plate ?? '—'} />
        <Row
          label="Frete"
          value={
            data.freight ? (
              <Link to={`/freights/${data.freight.id}`} className="text-primary hover:underline">
                F-{data.freight.code}
              </Link>
            ) : (
              '—'
            )
          }
        />
        <Row label="Fornecedor" value={data.supplier ?? '—'} />
        <Row label="Nº do documento" value={data.documentNumber ?? '—'} />
        <Row label="Observações" value={data.notes ?? '—'} />
        <Row label="Registrado em" value={formatDate(data.createdAt)} />
      </div>
    </div>
  )
}
